import React, { useState } from 'react';

/**
 * The reference page every rewritten page is checked against.
 *
 * It renders the design system against the live tokens, so "what does a table
 * look like here" has one answer instead of one per page. When a page is
 * rebuilt, the rule is to reach for what is on this page first and only add
 * something new when nothing here fits — at which point the new thing gets
 * added here too.
 *
 * It is also the cheapest possible smoke test for the theme: if a token is
 * wrong or missing, it shows up on this page before it reaches a real screen.
 */
export default function Design() {
  const [checked, setChecked] = useState(true);

  return (
    <div className="next-page">
      <header className="next-page__header">
        <h1>Design</h1>
        <p className="next-muted">
          The component inventory for the rebuilt admin. Every value here comes
          from the tokens in <code>next/styles/_tokens.scss</code>, scoped to this
          subtree — nothing on this page can affect the old admin.
        </p>
      </header>

      <section className="next-section">
        <h2>Colour</h2>
        <div className="next-swatches">
          {[
            ['Surface', 'var(--ws-surface)'],
            ['Sunken', 'var(--ws-surface-sunken)'],
            ['Border', 'var(--ws-border)'],
            ['Text', 'var(--ws-text)'],
            ['Muted', 'var(--ws-text-muted)'],
            ['Accent', 'var(--ws-accent)']
          ].map(([name, value]) => (
            <div className="next-swatch" key={name}>
              <span className="next-swatch__chip" style={{ background: value }} />
              <span className="next-swatch__name">{name}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="next-section">
        <h2>Buttons</h2>
        <div className="next-row">
          <button type="button" className="next-btn next-btn--primary">Primary</button>
          <button type="button" className="next-btn">Secondary</button>
          <button type="button" className="next-btn next-btn--quiet">Quiet</button>
          <button type="button" className="next-btn" disabled>Disabled</button>
        </div>
      </section>

      <section className="next-section">
        <h2>Form</h2>
        <div className="next-form">
          <label className="next-field">
            <span className="next-field__label">Text</span>
            <input className="next-input" defaultValue="Lyngby Idrætsby" />
          </label>
          <label className="next-field">
            <span className="next-field__label">Select</span>
            <select className="next-input" defaultValue="tennis">
              <option value="tennis">Tennis</option>
              <option value="padel">Padel</option>
            </select>
          </label>
          <label className="next-field next-field--inline">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => setChecked(value => !value)}
            />
            <span>Visible</span>
          </label>
        </div>
      </section>

      <section className="next-section">
        <h2>Table</h2>
        <table className="next-table next-table--stack">
          <thead>
            <tr>
              <th>Facility</th>
              <th>Area</th>
              <th className="next-table__num">Products</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Rudersdal Tennis', 'Holte', 1, 'live'],
              ['Farum Tennisklub', 'Farum', 2, 'live'],
              ['Lyngby Idrætsby', 'Lyngby', 1, 'off']
            ].map(([name, area, products, status]) => (
              <tr key={name}>
                <td data-label="">{name}</td>
                <td className="next-muted" data-label="Area">{area}</td>
                <td className="next-table__num" data-label="Products">{products}</td>
                <td data-label="Status">
                  <span className={`next-pill next-pill--${status}`}>{status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="next-section">
        <h2>Messages</h2>
        <div className="next-note next-note--info">Informational.</div>
        <div className="next-note next-note--warn">Something needs attention.</div>
        <div className="next-note next-note--error">Something failed.</div>
      </section>

      <section className="next-section">
        <h2>Active filters</h2>
        <p className="next-muted">
          What is narrowing a list, above the list. The filter card reads as empty at a
          glance, so without these a list of two rows looks like a list of two rows
          rather than like one facility’s worth. Each chip removes its own filter.
        </p>
        <div className="next-chips">
          <span className="next-chips__label">Filtered by</span>
          <span className="next-chip">
            <span className="next-chip__label">Status</span>
            <span className="next-chip__value">Booked</span>
            <span className="next-chip__x" aria-hidden>×</span>
          </span>
          <span className="next-chip">
            <span className="next-chip__label">Facility</span>
            <span className="next-chip__value">Hjortshøj-Egaa Tennis</span>
            <span className="next-chip__x" aria-hidden>×</span>
          </span>
          <span className="next-chip next-chip--clear">Clear all</span>
        </div>
      </section>

      <section className="next-section">
        <h2>List tools</h2>
        <p className="next-muted">
          Copy link, export and saved searches. None of them needed an endpoint: these
          lists keep their filters in the query string, so a search here is already a URL.
          The second export walks the remaining pages — capped, and the file says so when a
          cap stopped it, because a truncated spreadsheet that does not admit it looks
          exactly like missing data.
        </p>
        <div className="next-listtools">
          <span className="next-btn next-btn--sm">Saved searches (2)</span>
          <span className="next-btn next-btn--sm">Copy link</span>
          <span className="next-btn next-btn--sm">Export 15 rows</span>
          <span className="next-btn next-btn--sm">Export all matching</span>
        </div>
      </section>

      <section className="next-section">
        <h2>Secret code</h2>
        <p className="next-muted">
          A value somebody will read out loud — a door code, a reference dictated over the
          phone. Large enough to read at arm’s length, spaced so 8 and B do not blur, and
          always next to a copy button. Struck through when it has expired: still printed,
          because support may need to know which code was the dead one, but reading it out
          would not open anything.
        </p>
        <div className="next-code">
          <span className="next-code__value">481902</span>
          <span className="next-btn next-btn--quiet next-btn--sm">Copy</span>
        </div>
        <br />
        <div className="next-code">
          <span className="next-code__value next-code__value--dead">773401</span>
          <span className="next-stack__sub next-stack__sub--bad">expired 26 h ago</span>
        </div>
      </section>

      <section className="next-section">
        <h2>Back to a list</h2>
        <p className="next-muted">
          The link at the top of every detail page. It returns to the search you left
          rather than to the bare list, and says so — a link that quietly starts behaving
          differently is worse than one that states what it does.
        </p>
        <span className="next-back">
          ← All signups
          <span className="next-back__kept">· filters kept</span>
        </span>
      </section>

      <section className="next-section">
        <h2>Sortable column</h2>
        <p className="next-muted">
          A header is a button only where the endpoint can honestly order by that column.
          Most cannot, and they stay plain — an arrow that produces an order nobody can
          explain is worse than no arrow. Clicking the sorted column flips it.
        </p>
        <table className="next-table">
          <thead>
            <tr>
              <th><button type="button" className="next-th-sort">Total ↓</button></th>
              <th><button type="button" className="next-th-sort">Status</button></th>
              <th>Occurs at (not sortable)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>250.00 DKK</td>
              <td><span className="next-pill next-pill--ok">Settled</span></td>
              <td>2026-08-19 17:00</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="next-section">
        <h2>Keyboard</h2>
        <p className="next-muted">
          Three shortcuts, all of them for the same thing: getting somewhere without
          reading a thirty-item sidebar.
        </p>
        <div className="next-palette__foot" style={{ borderRadius: '8px', border: '1px solid var(--ws-border-faint)' }}>
          <span><kbd>Cmd</kbd><kbd>K</kbd> search anything</span>
          <span><kbd>/</kbd> the same, one-handed</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </section>
    </div>
  );
}
